/**
 * NewsCarouselTemplate — variação `newsCarousel` do bloco Listing nativo.
 *
 * "Latest News" da home (Proposta Layout 01, seção 6): card destaque grande
 * + faixa de miniaturas clicáveis + setas. A query/ordenação/limite vêm do
 * próprio Listing (querystring widget nativo) — nada é recriado aqui.
 */
import { useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import cx from 'clsx';
import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';
import Component from '@plone/volto/components/theme/Component/Component';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import leftKeySVG from '@plone/volto/icons/left-key.svg';
import rightKeySVG from '@plone/volto/icons/right-key.svg';
import { useCarousel } from '../../shared/Carousel';

const messages = defineMessages({
  label: {
    id: 'newsCarousel.label',
    defaultMessage: 'News carousel',
  },
  previous: {
    id: 'carousel.previous',
    defaultMessage: 'Previous slide',
  },
  next: {
    id: 'carousel.next',
    defaultMessage: 'Next slide',
  },
  showItem: {
    id: 'newsCarousel.showItem',
    defaultMessage: 'Show: {title}',
  },
  slideOf: {
    id: 'carousel.slideOf',
    defaultMessage: '{current} of {total}',
  },
});

const SWIPE_THRESHOLD = 40; // px
const AUTOPLAY_INTERVAL = 5000; // ms

// O PreviewImage do core monta o `srcSet` com todas as escalas do Plone, mas
// não emite `sizes` — sem ele o navegador assume 100vw e baixa a escala do
// tamanho da janela (1600w para um thumb de 132px). Larguras de
// _news-listing.scss: carrossel 1130px menos as duas setas (56+30 cada).
const FEATURED_SIZES = '(max-width: 1129px) 100vw, 958px';
const THUMB_SIZES = '(max-width: 768px) 96px, 132px';

type ListingItem = {
  '@id': string;
  title?: string;
  id?: string;
  description?: string;
  effective?: string;
  Subject?: string[];
  [key: string]: unknown;
};

export type NewsCarouselTemplateProps = {
  items: ListingItem[];
  isEditMode?: boolean;
  autoplay?: boolean;
};

const NewsCarouselTemplate = ({
  items,
  isEditMode,
  autoplay = false,
}: NewsCarouselTemplateProps) => {
  const intl = useIntl();
  const total = items.length;
  const { index, next, prev, goTo } = useCarousel(total);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (!autoplay || paused || reducedMotion || total < 2) return;
    const timer = setInterval(next, AUTOPLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [autoplay, paused, reducedMotion, total, next]);

  if (total === 0) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      next();
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD) return;
    if (delta < 0) next();
    else prev();
  };

  const featured = items[Math.min(index, total - 1)];

  return (
    <section
      className="news-carousel"
      aria-roledescription="carousel"
      aria-label={intl.formatMessage(messages.label)}
    >
      {/* Padrão WAI-ARIA APG de carrossel: teclado/touch no contêiner; os
          mesmos controles existem como botões nativos (setas/thumbs). */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="news-carousel__inner"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        onKeyDown={onKeyDown}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="news-carousel__featured">
          <div
            role="group"
            aria-roledescription="slide"
            aria-label={intl.formatMessage(messages.slideOf, {
              current: index + 1,
              total,
            })}
          >
            <ConditionalLink item={featured} condition={!isEditMode}>
              <div className="news-carousel__featured-media">
                <Component
                  componentName="PreviewImage"
                  item={featured}
                  alt=""
                  loading="lazy"
                  sizes={FEATURED_SIZES}
                />
                <div className="news-carousel__featured-overlay">
                  <h3 className="news-carousel__featured-title">
                    {featured.title || featured.id}
                  </h3>
                </div>
              </div>
            </ConditionalLink>
          </div>

          {total > 1 && (
            <>
              <button
                type="button"
                className="news-carousel__arrow news-carousel__arrow--prev"
                onClick={prev}
                aria-label={intl.formatMessage(messages.previous)}
              >
                <Icon name={leftKeySVG} size="24px" />
              </button>
              <button
                type="button"
                className="news-carousel__arrow news-carousel__arrow--next"
                onClick={next}
                aria-label={intl.formatMessage(messages.next)}
              >
                <Icon name={rightKeySVG} size="24px" />
              </button>
            </>
          )}
        </div>

        {total > 1 && (
          <ul className="news-carousel__thumbs">
            {items.map((item, i) => (
              <li className="news-carousel__thumb-item" key={item['@id']}>
                <button
                  type="button"
                  className={cx('news-carousel__thumb', {
                    'news-carousel__thumb--active': i === index,
                  })}
                  onClick={() => goTo(i)}
                  aria-label={intl.formatMessage(messages.showItem, {
                    title: item.title || item.id,
                  })}
                  aria-current={i === index}
                >
                  <Component
                    componentName="PreviewImage"
                    item={item}
                    alt=""
                    loading="lazy"
                    sizes={THUMB_SIZES}
                  />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default NewsCarouselTemplate;
